import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Download, ScanLine, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TM } from "@/components/TM";
import { fetchDownloadSettings } from "@/lib/api";

const META = {
  android: { label: "Android", icon: "fa-brands fa-android", store: "Google Play" },
  ios: { label: "iOS", icon: "fa-brands fa-apple", store: "App Store" },
};

export const DownloadModal = ({ open, onOpenChange }) => {
  const [tab, setTab] = useState("android");
  const { data: settings } = useQuery({ queryKey: ["download-settings"], queryFn: fetchDownloadSettings, enabled: open, staleTime: 60_000 });
  const p = { ...META[tab], ...(settings?.[tab] || {}) };
  const url = p.url || window.location.origin;
  const showUploaded = p.qr_mode === "upload" && p.qr_image;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border-0 bg-[#FBF8F4] shadow-2xl" data-testid="download-modal">
        <div className="p-7 sm:p-9">
          <p className="eyebrow mb-3">Get the app</p>
          <DialogTitle className="font-display text-3xl tracking-tight text-[var(--ink)]">
            Get the TruCitizen<TM /> app
          </DialogTitle>
          <DialogDescription className="mt-2 text-[var(--ink-soft)]">
            Scan the QR or tap download. Pick your platform.
          </DialogDescription>

          <div className="mt-6 grid grid-cols-2 p-1 rounded-full bg-black/[0.06]" role="tablist">
            {Object.entries(META).map(([key, pl]) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                data-testid={`modal-tab-${key}`}
                className={`relative py-2.5 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-colors ${tab === key ? "text-white" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}
              >
                {tab === key && (
                  <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-[var(--ink)]" transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                )}
                <i className={`${pl.icon} relative z-10 text-base`} aria-hidden="true" />
                <span className="relative z-10">{pl.label}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="mt-7 flex flex-col items-center"
              data-testid={`modal-panel-${tab}`}
            >
              <div className="relative p-4 rounded-2xl bg-white shadow-[0_25px_50px_-25px_rgba(15,23,42,0.35)] border border-black/5">
                {showUploaded ? (
                  <img src={p.qr_image} alt={`${p.label} QR code`} width={176} height={176} className="w-[176px] h-[176px] object-contain" data-testid="download-qr-image" />
                ) : (
                  <QRCodeSVG value={url} size={176} level="H" fgColor="#0F172A" bgColor="#FFFFFF" imageSettings={{ src: "/logo.png", height: 24, width: 68, excavate: true }} />
                )}
                {p.coming_soon && (
                  <span className="absolute -top-2 -right-2 px-2.5 py-1 rounded-full bg-[var(--ink)] text-white text-[10px] font-mono uppercase tracking-wider flex items-center gap-1" data-testid="download-coming-soon">
                    <Clock size={10} /> Coming soon
                  </span>
                )}
              </div>
              <p className="mt-4 flex items-center gap-2 text-xs text-[var(--muted)] font-mono uppercase tracking-widest">
                <ScanLine size={14} /> Scan with your phone
              </p>
              {p.coming_soon ? (
                <span className="mt-6 w-full btn-ghost justify-center cursor-default" data-testid="download-action-btn">
                  <i className={`${p.icon} text-xl`} aria-hidden="true" />
                  <span>Coming soon on {p.store}</span>
                </span>
              ) : (
                <a href={url} target="_blank" rel="noopener noreferrer" data-testid="download-action-btn" className="mt-6 w-full btn-saffron">
                  <i className={`${p.icon} text-xl`} aria-hidden="true" />
                  <span>Download on {p.store}</span>
                  <Download size={18} />
                </a>
              )}
              {p.note && <p className="mt-3 text-xs text-[var(--muted)]">{p.note}</p>}
            </motion.div>
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
